/**
 * WebAuthn glue for Kratos's `passkey` method (discoverable
 * credentials). Kratos ships a script node that drives
 * `navigator.credentials` itself — we deliberately do NOT inject it.
 * Instead the challenge JSON in the flow's hidden nodes is parsed and
 * the ceremony is driven here, so the UI stays 100% ours and the
 * payload shape stays pinned by our own tests.
 *
 * Field contract (Kratos v1.x passkey method):
 * - login:        hidden node `passkey_challenge` (request options)
 *                 → submit  `passkey_login` (serialized assertion)
 * - registration: hidden node `passkey_create_data` (creation options)
 *                 → submit  `passkey_register` (serialized attestation)
 * - settings:     hidden node `passkey_create_data` (creation options)
 *                 → submit  `passkey_settings_register` (NOT
 *                 `passkey_register` — the settings strategy uses its
 *                 own field, and there is no display-name field).
 * All binary fields travel base64url without padding.
 */

import type { KratosFlow } from "@/lib/kratos";
import { nodeByName } from "@/lib/kratos";

function base64UrlToBytes(value: string): Uint8Array {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function bytesToBase64Url(buffer: ArrayBuffer | null): string | null {
  if (!buffer) return null;
  const bytes = new Uint8Array(buffer);
  let raw = "";
  for (const byte of bytes) raw += String.fromCharCode(byte);
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

type RawPublicKeyOptions = Record<string, unknown> & {
  publicKey: Record<string, unknown>;
};

function parseOptionsNode(flow: KratosFlow, nodeName: string): RawPublicKeyOptions {
  const value = nodeByName(flow, nodeName)?.attributes.value;
  if (typeof value !== "string" || !value) {
    throw new Error(`Kratos flow has no ${nodeName} challenge`);
  }
  return JSON.parse(value) as RawPublicKeyOptions;
}

function decodeRequestOptions(raw: RawPublicKeyOptions): PublicKeyCredentialRequestOptions {
  const publicKey = raw.publicKey as Record<string, unknown>;
  const decoded: Record<string, unknown> = {
    ...publicKey,
    challenge: base64UrlToBytes(String(publicKey.challenge)),
    allowCredentials: (
      (publicKey.allowCredentials as Array<Record<string, unknown>> | undefined) ?? []
    ).map((cred) => ({
      ...cred,
      id: base64UrlToBytes(String(cred.id)),
    })),
  };
  return decoded as unknown as PublicKeyCredentialRequestOptions;
}

function decodeCreationOptions(raw: RawPublicKeyOptions): PublicKeyCredentialCreationOptions {
  const publicKey = raw.publicKey as Record<string, unknown>;
  const user = publicKey.user as Record<string, unknown>;
  const decoded: Record<string, unknown> = {
    ...publicKey,
    challenge: base64UrlToBytes(String(publicKey.challenge)),
    user: {
      ...user,
      id: base64UrlToBytes(String(user.id)),
    },
    excludeCredentials: (
      (publicKey.excludeCredentials as Array<Record<string, unknown>> | undefined) ?? []
    ).map((cred) => ({
      ...cred,
      id: base64UrlToBytes(String(cred.id)),
    })),
  };
  return decoded as unknown as PublicKeyCredentialCreationOptions;
}

export function serializeAssertion(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bytesToBase64Url(response.authenticatorData),
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      signature: bytesToBase64Url(response.signature),
      userHandle: bytesToBase64Url(response.userHandle),
    },
  });
}

export function serializeAttestation(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAttestationResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bytesToBase64Url(response.attestationObject),
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
    },
  });
}

export function flowSupportsPasskeyLogin(flow: KratosFlow): boolean {
  return !!nodeByName(flow, "passkey_challenge")?.attributes.value;
}

export function flowSupportsPasskeyRegistration(flow: KratosFlow): boolean {
  return !!nodeByName(flow, "passkey_create_data")?.attributes.value;
}

export function webauthnSupported(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export async function conditionalUiAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  const probe = window.PublicKeyCredential.isConditionalMediationAvailable;
  if (!probe) return false;
  try {
    return await probe();
  } catch {
    return false;
  }
}

/** Run the passkey assertion ceremony for a login flow. Returns the
 * value to submit as `passkey_login`. Pass `conditional: true` (with an
 * AbortSignal) for autofill — the promise resolves when the user picks
 * a credential from the input's autofill UI. */
export async function passkeyLogin(
  flow: KratosFlow,
  opts: { conditional?: boolean; signal?: AbortSignal } = {},
): Promise<string> {
  const publicKey = decodeRequestOptions(parseOptionsNode(flow, "passkey_challenge"));
  const credential = (await navigator.credentials.get({
    publicKey,
    signal: opts.signal,
    ...(opts.conditional ? { mediation: "conditional" as CredentialMediationRequirement } : {}),
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error("Passkey sign-in was cancelled");
  return serializeAssertion(credential);
}

/** Run the passkey creation ceremony for a registration/settings flow.
 * Submit the result as `passkey_register` (registration flows) or
 * `passkey_settings_register` (settings flows). */
export async function passkeyRegister(flow: KratosFlow): Promise<string> {
  const publicKey = decodeCreationOptions(parseOptionsNode(flow, "passkey_create_data"));
  const credential = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error("Passkey creation was cancelled");
  return serializeAttestation(credential);
}
